import { AfterViewInit, Component, HostBinding, OnInit, QueryList, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { isObject } from '../../../models/utils';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { Corpus } from '../../../models/corpus';
import { Custodian } from '../../../models/custodian';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { DynamicFolder } from '../../../models/dynamic-folder';
import { Endpoint, EntityType, HelpIndex, UpdateStatusType } from '../../../models/enums';
import { AlignDirection, CellTemplateType, FilterTemplateType, GenericColDef, GridContext, GridRowSelectionType, SortByFilter } from '../../../models/grid-context';
import { ActionType, ObjectActionListType, Permissions } from '../../../models/permission';
import { WorkflowReport } from '../../../models/report';
import { Tag } from '../../../models/tag';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { GenericGridComponent } from '../../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';
import { GenericTreeComponent } from '../../generic-tree/generic-tree.component';
import { CellClickedEvent, SelectionChangedEvent } from 'ag-grid-community';
import * as _ from 'underscore';
import { DomainList } from '../../../models/domain-lists';
import { CreateEditEntityModalComponent } from '../create-edit-entity-modal/create-edit-entity-modal.component';
import { AddSelectedTextToModalComponent, AddSelectedTextToModalData, AddSelectedTextToModalType } from '../add-selected-text-to-modal/add-selected-text-to-modal.component';
import { DOMAINS_MAX_DISPLAY_GRID_ENTRIES, METADATA_MAX_DISPLAY_GRID_ENTRIES, SYS_GENERATED_DOMAIN_LIST } from '../../../models/constants';

@Component({
    standalone: false,
    selector: 'res-generic-picker-modal',
    templateUrl: './generic-picker-modal.component.html',
    styleUrls: ['./generic-picker-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class GenericPickerModalComponent extends BaseModalComponent implements OnInit, AfterViewInit {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChildren(GenericGridComponent) genericGrids: QueryList<GenericGridComponent>;
    @ViewChild(GenericTreeComponent, { static: true }) tree: GenericTreeComponent;

    updateStatusType: UpdateStatusType;

    isBusy = false;
    isBusyEntries = false;
    isBusyMessage = '';
    isBusyMessageEntries = '';
    modalTitle = '';

    modalAutomationId = '';
    entriesAutoId = '';
    domainOrAliasContainerAutoId = {
        left: '',
        right: ''
    };
    secondaryButtonLabel = 'Cancel';
    primaryButtonLabel = 'OK';
    modalClass = 'generic-picker-modal';
    modalHelpIndex = HelpIndex.myedisc_custodian_select_adv_search_dlg;
    requestInfo: DrRequestInfo;
    gridName = '';
    rightGridName = '';
    targetType = '';
    columnDefs: Array<GenericColDef>;
    columnDefsLists: Array<GenericColDef>;
    includeSearchTermReport: boolean;
    provideHTMLSizes: boolean;
    searchTermVisible = true;
    selectedList: any;
    rowsSelected: any[] = [];
    rightGridSelectedRows: any[] = [];
    currentEntries: any;
    filterQuery = '';
    lists = [];
    leftGridEntriesCount = '';
    domainButtonDisabled: boolean;
    domainsCount: string;
    needsBackendFiltering = false;
    leftListTotalCount = 0;
    domainEntriesTotalCount = 0;

    // Track domain selections per domain list (key: domainListHandle, value: array of selected domain names)
    selectedDomainsPerList: Map<string, string[]> = new Map();
    // Track total domain count per domain list for determining partial selection state
    totalDomainsPerList: Map<string, number> = new Map();
    // Track domain lists selected as whole lists (when checkbox is checked but no individual domains selected OR empty list)
    // key: domainListHandle, value: { name: domainListName, selected: boolean }
    selectedWholeDomainLists: Map<string, { name: string; selected: boolean }> = new Map();
    // Track fully selected domain lists (when ALL domains are selected - show DL name in query)
    // key: domainListHandle, value: { name: domainListName }
    fullySelectedDomainLists: Map<string, { name: string }> = new Map();

    // Flag to prevent onSelectionChanged from triggering selectAll when not clicking checkbox
    private isProcessingLeftGridSelection = false;
    // Track the previous selection state to detect actual checkbox clicks
    private previousLeftGridSelectedHandles: Set<string> = new Set();

    leftListGridContext = new GridContext({
        inlineGridFilter: true,
        sortBy: SortByFilter.NAME.toString().toLowerCase(),
        sortAscending: true,
                onFilterApply: (queryStr: string, filterType: FilterTemplateType, filterColumnName: string) => this.onLeftFilterApply(queryStr, filterType, filterColumnName),
    });

    rightListGridContext = new GridContext({
        inlineGridFilter: true,
        onFilterApply: (queryStr: string, filterType: FilterTemplateType, filterColumnName: string) => this.onFilterApply(queryStr, filterType, filterColumnName),
    });

    modalEntityType = {
        TAGS: {
            type: 'tags', title: 'Select Tags', gridName: 'Tags', modalAutomationId: 'generic-picker-modal-tags',
            requestInfo: new DrRequestInfo({
                endpoint: Endpoint.workingSetManager_listWorkingSetViews,
                responseName: 'views',
                data: {
                    filters: [{ attribute: 'viewType', value: 'TAG', operator: 'EQUALS' }],
                    workingSetHandle: this.commonService.projectData.handle,
                    startIndex: 0, count: 0
                }
            })
        },
        CUSTODIANS: {
            type: 'custodians', title: 'Select Custodians', gridName: 'Custodians', modalAutomationId: 'generic-picker-modal-custodians',
            requestInfo: new DrRequestInfo({
                endpoint: Endpoint.ecaManager_listCustodians,
                responseName: 'custodians',
                data: {
                    projectHandle: this.commonService.currentProject.handle
                }
            })
        },
        FOLDERS: {
            type: 'folders', title: 'Select Folders', gridName: 'Folders', modalAutomationId: 'generic-picker-modal-folders',
            requestInfo: new DrRequestInfo({
                endpoint: Endpoint.projectManager_listDynamicFolders,
                responseName: 'dynamicFolders',
                data: {
                    projectHandle: this.commonService.currentProject.handle
                }
            })
        },
        DOMAINS: {
            type: 'domains', title: 'Select Domain Lists', gridName: 'Domains', modalAutomationId: 'generic-picker-modal-domains',
            requestInfo: new DrRequestInfo({
                endpoint: Endpoint.projectManager_listDomains,
                responseName: 'domains',
                data: {
                    projectHandle: this.commonService.currentProject.handle,
                    count: 0
                }
            })
        },
        ALIAS: {
            type: 'alias-list', title: 'Select Alias Lists', gridName: 'Alias Lists', modalAutomationId: 'generic-picker-modal-alias-lists',
            requestInfo: new DrRequestInfo({
                endpoint: Endpoint.projectManager_listAliasList,
                responseName: 'aliasList',
                data: {
                    handle: this.commonService.currentProject.handle
                }
            })
        },
        DATASETS: {
            type: 'datasets', title: 'Select Data Set/s', gridName: 'Data Sets', modalAutomationId: 'generic-picker-modal-data-sets',
            helpIndex: HelpIndex.myedisc_sourcedata_select_ad_search_dlg,
            requestInfo: new DrRequestInfo({
                endpoint: Endpoint.workingSetManager_listWorkingSetCorpora,
                responseName: 'corpora',
                data: {
                    handle: this.commonService.projectData.handle
                }
            })
        },
        EXPORT_COMPARISON: {
            type: 'export-comparison', title: 'Create Export Comparison Report: Select Export',
            modalAutomationId: 'target-picker-tree', helpIndex: HelpIndex.generate_export_comparison_report_dlg
        },
        SENDERS: {
            type: 'senders', title: 'Select Senders', gridName: 'Senders', modalAutomationId: 'generic-picker-modal-senders',
            helpIndex: HelpIndex.myedisc_advanced_search
        },
        RECIPIENTS: {
            type: 'recipients', title: 'Select Recipients', gridName: 'Recipients', modalAutomationId: 'generic-picker-modal-recipients',
            helpIndex: HelpIndex.myedisc_advanced_search
        },
        METADATA: {
            type: 'metadata', title: 'Select ', gridName: 'Metadata', modalAutomationId: 'generic-picker-modal-metadata',
            helpIndex: HelpIndex.myedisc_advanced_search
        }
    };

    rowClassRules = {
        'custom-selected-row': (params) => {
            if ((this.modalData.entityType === this.modalEntityType.ALIAS.type || this.modalData.entityType === this.modalEntityType.DOMAINS.type) &&
                params?.data?.isRowSelected) {
                return true;
            }
            return false;
        },
        'indeterminate-checkbox-row': (params) => {
            // Apply indeterminate class when domain list has partial domain selections
            if (this.modalData.entityType === this.modalEntityType.DOMAINS.type && params?.data?.partiallySelected) {
                return true;
            }
            return false;
        }
    };

    rowSelection = GridRowSelectionType.multipleCheckboxWithRowClickSelection;
    rightGridRowSelection = GridRowSelectionType.multipleCheckboxWithRowClickSelection;

    nodes = new Array<GenericTreeNode>();
    selectedNode: GenericTreeNode;
    inputAutoFocused: boolean;

    constructor(
        private modalService: BsModalService,
        private restService: RestService,
        private commonService: CommonService,
        private modalRef: BsModalRef,
    ) {
        super(modalService, modalRef);
    }

    ngOnInit(): void {
        const modalData = this.modalData;

        switch (this.modalData.entityType) {
            case this.modalEntityType.ALIAS.type: {
                this.modalAutomationId = 'alias-lists';
                this.domainOrAliasContainerAutoId.left = this.modalAutomationId + '-container';
                this.domainOrAliasContainerAutoId.right = 'alias-entries-grid-container';
                this.rowSelection = GridRowSelectionType.multipleCheckbox;
                break;
            }
            case this.modalEntityType.DOMAINS.type: {
                this.modalAutomationId = 'domain-lists';
                this.domainOrAliasContainerAutoId.left = this.modalAutomationId + '-container';
                this.domainOrAliasContainerAutoId.right = 'domain-entries-grid-container';
                this.rowSelection = GridRowSelectionType.multipleCheckbox;

                // Restore selections from previous modal session if available
                if (modalData.selectedDomainsPerList) {
                    this.selectedDomainsPerList = new Map(Object.entries(modalData.selectedDomainsPerList));
                }
                if (modalData.totalDomainsPerList) {
                    this.totalDomainsPerList = new Map(Object.entries(modalData.totalDomainsPerList));
                }
                if (modalData.selectedWholeDomainLists) {
                    this.selectedWholeDomainLists = new Map(
                        Object.entries(modalData.selectedWholeDomainLists).map(([key, value]: [string, any]) => [key, value])
                    );
                }
                if (modalData.fullySelectedDomainLists) {
                    this.fullySelectedDomainLists = new Map(
                        Object.entries(modalData.fullySelectedDomainLists).map(([key, value]: [string, any]) => [key, value])
                    );
                }
                break;
            }
            default: {
                this.domainOrAliasContainerAutoId.right = this.modalAutomationId + '-right-container';
            }
        }
        this.columnDefs = new Array<GenericColDef>(
            {
                colId: 'checkbox',
                automationId: this.modalAutomationId,
                minWidth: 36, maxWidth: 36, width: 36,
                headerCheckboxSelection: this.modalData?.entityType === this.modalEntityType.DOMAINS.type ? false : true,
                checkboxSelection: params => params.data.name !== SYS_GENERATED_DOMAIN_LIST,
                suppressSizeToFit: true,
            },
            {
                headerName: this.modalData.entityType === this.modalEntityType.DATASETS.type ? 'Data Set Name' :
                    this.modalData.entityType === this.modalEntityType.DOMAINS.type ? 'Domain List Name' :
                        this.modalData.entityType === this.modalEntityType.ALIAS.type ? 'Alias List Name' : 'Name',
                field: 'name',
                automationId: this.modalAutomationId,
                sortBy: SortByFilter.NAME.toString().toLowerCase(),
                lockVisible: true,
                cellTemplateType: CellTemplateType.nameIconLabel,
                filterType: FilterTemplateType.freeform,
                headerAlignDirection: AlignDirection.left,
                suppressMovable: true,
                serviceTierFilter: this.modalData.entityType === this.modalEntityType.CUSTODIANS.type
            },
        );

        this.columnDefsLists = new Array<GenericColDef>(
            {
                headerName: modalData.entityType === this.modalEntityType.DOMAINS.type ? 'Domain Name' : 'Aliases',
                field: modalData.entityType === this.modalEntityType.DOMAINS.type ? 'name' : 'aliasName',
                automationId: modalData.entityType === this.modalEntityType.DOMAINS.type ? 'domain-lists' + this.entriesAutoId : 'alias-lists' + this.entriesAutoId,
                colId: modalData.entityType === this.modalEntityType.DOMAINS.type ? 'domainListCustomAutomationId' : 'aliasListCustomAutomationId',
                filterType: FilterTemplateType.freeform,
                suppressMovable: true
            }
        );
        if (modalData.entityType === this.modalEntityType.DOMAINS.type) {
            this.addCheckboxColumnToRightGrid();
        }

        if (this.modalData.entityType === this.modalEntityType.DATASETS.type) {
            this.columnDefs.push(
                {
                    headerName: 'Index Level',
                    field: 'representationTypeDisplay',
                    automationId: this.modalAutomationId,
                    lockVisible: true,
                    cellTemplateType: CellTemplateType.nameIconLabel,
                    hide: this.modalData.entityType !== this.modalEntityType.DATASETS.type,
                    headerAlignDirection: AlignDirection.left,
                    cellAlignDirection: AlignDirection.left,
                }
            );
        }

        if (this.modalData.entityType === this.modalEntityType.TAGS.type ||
            this.modalData.entityType === this.modalEntityType.CUSTODIANS.type ||
            this.modalData.entityType === this.modalEntityType.FOLDERS.type ||
            this.modalData.entityType === this.modalEntityType.DATASETS.type) {
            this.columnDefs.push(
                {
                    headerName: 'Documents',
                    field: 'documentCount',
                    automationId: this.modalAutomationId,
                    headerAlignDirection: AlignDirection.right,
                    cellAlignDirection: AlignDirection.right,
                }
            );
        }

        if (this.modalData.entityType === this.modalEntityType.ALIAS.type ||
            this.modalData.entityType === this.modalEntityType.DOMAINS.type) {
            this.columnDefs.push(
                {
                    headerName: 'Description',
                    field: 'description',
                    automationId: this.modalAutomationId,
                    headerAlignDirection: AlignDirection.left,
                    cellAlignDirection: AlignDirection.left,
                }
            );
        }

        switch (modalData.entityType) {
            case 'tags': {
                this.gridName = this.modalEntityType.TAGS.gridName;
                this.modalAutomationId = this.modalEntityType.TAGS.modalAutomationId;
                this.requestInfo = this.modalEntityType.TAGS.requestInfo;
                this.modalTitle = this.modalEntityType.TAGS.title;
                this.targetType = this.modalEntityType.TAGS.type;
                break;
            }
            case 'custodians': {
                this.gridName = this.modalEntityType.CUSTODIANS.gridName;
                this.modalAutomationId = this.modalEntityType.CUSTODIANS.modalAutomationId;
                this.requestInfo = this.modalEntityType.CUSTODIANS.requestInfo;
                this.modalTitle = this.modalEntityType.CUSTODIANS.title;
                this.targetType = this.modalEntityType.CUSTODIANS.type;
                break;
            }
            case 'folders': {
                this.gridName = this.modalEntityType.FOLDERS.gridName;
                this.modalAutomationId = this.modalEntityType.FOLDERS.modalAutomationId;
                this.requestInfo = this.modalEntityType.FOLDERS.requestInfo;
                this.modalTitle = this.modalEntityType.FOLDERS.title;
                this.targetType = this.modalEntityType.FOLDERS.type;
                this.rowSelection = this.modalData.selectionType ? this.modalData.selectionType : GridRowSelectionType.multipleCheckboxWithRowClickSelection;
                this.modalHelpIndex = this.modalData.type === EntityType.workFlow ? HelpIndex.myedisc_edit_run_open_workflow_dlg : HelpIndex.myedisc_custodian_select_adv_search_dlg;
                break;
            }
            case 'domains': {
                this.modalClass += ' modal-right-list';
                this.gridName = this.modalEntityType.DOMAINS.gridName;
                this.rightGridName = 'Domains Entries';
                this.modalAutomationId = this.modalEntityType.DOMAINS.modalAutomationId;
                this.entriesAutoId = 'domain-entries';
                this.requestInfo = this.modalEntityType.DOMAINS.requestInfo;
                this.modalTitle = this.modalEntityType.DOMAINS.title;
                this.targetType = this.modalEntityType.DOMAINS.type;
                break;
            }
            case 'alias-list': {
                this.modalClass += ' modal-right-list';
                this.gridName = this.modalEntityType.ALIAS.gridName;
                this.rightGridName = 'Aliases Entries';
                this.entriesAutoId = 'aliases-entries';
                this.modalAutomationId = this.modalEntityType.ALIAS.modalAutomationId;
                this.requestInfo = this.modalEntityType.ALIAS.requestInfo;
                this.modalTitle = this.modalEntityType.ALIAS.title;
                this.targetType = this.modalEntityType.ALIAS.type;
                break;
            }
            case 'datasets': {
                this.gridName = this.modalEntityType.DATASETS.gridName;
                this.modalAutomationId = this.modalEntityType.DATASETS.modalAutomationId;
                this.requestInfo = this.modalEntityType.DATASETS.requestInfo;
                this.modalTitle = this.modalEntityType.DATASETS.title;
                this.targetType = this.modalEntityType.DATASETS.type;
                this.modalHelpIndex = this.modalEntityType.DATASETS.helpIndex;
                this.modalClass += ' data-set-modal';
                break;
            }
            case 'recipients': {
                this.gridName = this.modalEntityType.RECIPIENTS.gridName;
                this.modalAutomationId = this.modalEntityType.RECIPIENTS.modalAutomationId;
                this.modalTitle = this.modalEntityType.RECIPIENTS.title;
                this.targetType = this.modalEntityType.RECIPIENTS.type;
                this.modalHelpIndex = this.modalEntityType.RECIPIENTS.helpIndex;
                this.modalClass += ' recipients-modal';
                Object.assign(this.leftListGridContext, { onFilterApply: (queryStr: string, filterType: FilterTemplateType, filterColumnName: string) => this.onMetadataFilterApply(queryStr, filterType, filterColumnName) });
                break;
            }
            case 'senders': {
                this.gridName = this.modalEntityType.SENDERS.gridName;
                this.modalAutomationId = this.modalEntityType.SENDERS.modalAutomationId;
                this.modalTitle = this.modalEntityType.SENDERS.title;
                this.targetType = this.modalEntityType.SENDERS.type;
                this.modalHelpIndex = this.modalEntityType.SENDERS.helpIndex;
                this.modalClass += ' senders-modal';
                Object.assign(this.leftListGridContext, { onFilterApply: (queryStr: string, filterType: FilterTemplateType, filterColumnName: string) => this.onMetadataFilterApply(queryStr, filterType, filterColumnName) });
                break;
            }
            case 'export-comparison': {
                this.modalAutomationId = this.modalEntityType.EXPORT_COMPARISON.modalAutomationId;
                this.modalTitle = this.modalEntityType.EXPORT_COMPARISON.title;
                this.targetType = this.modalEntityType.EXPORT_COMPARISON.type;
                this.primaryButtonLabel = this.modalData && this.modalData.items && this.modalData.items instanceof WorkflowReport ? 'OK' : 'Create Report';
                this.searchTermVisible = this.modalData && this.modalData.items && this.modalData.items instanceof WorkflowReport ? false : true;
                this.modalHelpIndex = this.modalEntityType.EXPORT_COMPARISON.helpIndex;
                this.inputAutoFocused = true;
                this.loadExportComparison(Permissions.checkUserPermission(ObjectActionListType.CORPUS_SET, ActionType.CREATE));
                break;
            }
            case 'metadata': {
                this.gridName = this.modalEntityType.METADATA.gridName;
                this.modalAutomationId = this.modalEntityType.METADATA.modalAutomationId + '-' + this.modalData.selectedMetadata;
                this.modalTitle = this.modalEntityType.METADATA.title + this.modalData.selectedMetadata;
                this.targetType = this.modalEntityType.METADATA.type;
                this.modalHelpIndex = this.modalEntityType.METADATA.helpIndex;
                this.modalClass += ' metadata-modal';
                Object.assign(this.leftListGridContext, { onFilterApply: (queryStr: string, filterType: FilterTemplateType, filterColumnName: string) => this.onMetadataFilterApply(queryStr, filterType, filterColumnName) });
                break;
            }
        }
    }

    ngAfterViewInit(): void {
        if ((this.modalData.entityType === this.modalEntityType.SENDERS.type ||
            this.modalData.entityType === this.modalEntityType.RECIPIENTS.type ||
            this.modalData.entityType === this.modalEntityType.METADATA.type) && this.modalData.metadataObject && this.genericGrids?.first) {

            const values = this.modalData.metadataObject.values.map((m: string) => { return { name: m } });
            this.currentEntries = [...new Set(values)];
            this.genericGrids.first.setData(values);

            this.leftListTotalCount = this.modalData.metadataObject.totalCount;
            this.needsBackendFiltering = this.leftListTotalCount > (this.modalData.entityType === this.modalEntityType.METADATA.type ? METADATA_MAX_DISPLAY_GRID_ENTRIES : DOMAINS_MAX_DISPLAY_GRID_ENTRIES);
            this.setLeftGridEntriesCount(values.length, this.leftListTotalCount);
        }
    }

    addCheckboxColumnToRightGrid(): void {
        this.columnDefsLists.unshift({
            colId: 'checkboxRight',
            automationId: this.modalAutomationId,
            minWidth: 36, maxWidth: 36, width: 36,
            headerCheckboxSelection: false,
            checkboxSelection: true,
            suppressSizeToFit: true,
        },);
    }

    responseArrayCallback = (response: any) => {
        switch (this.targetType) {
            case 'tags': {
                const displayColor = response.attributes.find(a => a.name === 'displayValue');
                if (displayColor) {
                    response.iconColor = displayColor.value;
                }
                return new Tag(response);
            }
            case 'custodians': {
                return new Custodian(response);
            }
            case 'folders': {
                const folder = new DynamicFolder(response);
                if (folder && !folder.view) {
                    folder.documentCount = 0;
                }
                return folder;
            }
            case 'domains': {
                response.iconClass = `fa-at`;
                response.iconColor = 'color-grey-10';
                return response;
            }
            case 'alias-list': {
                response.iconClass = `fa-address-book-o`;
                response.iconColor = 'color-blue-01';
                return response;
            }
            case 'datasets': {
                return new Corpus(response);
            }
        }
    }

    loadDataFinishedCallback = () => {
        if (this.targetType === this.modalEntityType.DOMAINS.type) {
            //Set system generated domain first in grid all the time
            let domainLists = this.genericGrids?.first?.getData();
            if (domainLists?.length > 0) {
                const sysDomIdx = domainLists.findIndex(domain => domain.name === SYS_GENERATED_DOMAIN_LIST);
                if (sysDomIdx > -1 && domainLists[sysDomIdx] !== domainLists[0]) {
                    const systemGenerateDom = domainLists[sysDomIdx];
                    domainLists = domainLists.filter(item => item.name !== SYS_GENERATED_DOMAIN_LIST);
                    domainLists.unshift(systemGenerateDom);
                    if (this.genericGrids.first.gridApi) {
                        this.genericGrids.first.gridApi.setGridOption('rowData', domainLists);
                    }
                }
            }

            // Restore checkbox states for domain lists based on previously selected domains
            if (this.selectedDomainsPerList.size > 0) {
                this.selectedDomainsPerList.forEach((selectedDomains, domainListHandle) => {
                    if (selectedDomains.length > 0) {
                        this.updateLeftGridCheckboxState(domainListHandle);
                    }
                });
            }

            // Restore checkbox states for whole domain list selections (lists with no individual domains)
            if (this.selectedWholeDomainLists.size > 0) {
                this.selectedWholeDomainLists.forEach((value, domainListHandle) => {
                    if (value.selected) {
                        this.genericGrids.first?.gridApi?.forEachNode(node => {
                            if (node.data.handle === domainListHandle) {
                                node.setSelected(true);
                            }
                        });
                    }
                });
            }

            // Restore checkbox states for fully selected domain lists
            if (this.fullySelectedDomainLists.size > 0) {
                this.fullySelectedDomainLists.forEach((_value, domainListHandle) => {
                    this.genericGrids.first?.gridApi?.forEachNode(node => {
                        if (node.data.handle === domainListHandle) {
                            node.setSelected(true);
                            node.data.partiallySelected = false;
                        }
                    });
                });
            }

            // Skip the default modalData.items logic when restoring from maps
            if (this.selectedDomainsPerList.size > 0 || this.selectedWholeDomainLists.size > 0 || this.fullySelectedDomainLists.size > 0) {
                return;
            }
        }
        if (this.modalData && this.modalData.items && this.modalData.items.length > 0) {
            this.modalData.items.forEach((item) => {
                this.genericGrids.first.gridApi.forEachNode((node) => {
                    if (isObject(item) && node.data.name === item.name) {
                        node.setSelected(true);
                    } else if (node.data.name === item) {
                        node.setSelected(true);
                    } else if (node.data.name === this.selectedList?.name) {
                        node.setSelected(true);
                    }
                });
            });
        }
        if (this.targetType === this.modalEntityType.DOMAINS.type || this.targetType === this.modalEntityType.ALIAS.type) {
            if (this.rowsSelected.length > 0) {
                this.rowsSelected.forEach(row => {
                    this.genericGrids.first.findNode((node) => node.data?.handle === row.handle, true);
                });
            }
            if (this.selectedList) {
                let node = this.genericGrids.first.getData().find(node => { return node.handle === this.selectedList.handle });
                if (node) {
                    node.isRowSelected = true;
                    this.genericGrids.first.gridApi?.redrawRows();
                }
            }
        }
    }

    selectedRows(): any {
        let selRows = this.genericGrids?.first?.gridApi?.getSelectedRows()?.length > 0;
        if (this.targetType === this.modalEntityType.DOMAINS.type) {
            // Check if any domains are selected across all domain lists
            let hasSelectedDomains = false;
            this.selectedDomainsPerList.forEach((domains) => {
                if (domains.length > 0) {
                    hasSelectedDomains = true;
                }
            });
            // Also check current right grid selections
            if (this.genericGrids?.last?.gridApi?.getSelectedRows()?.length > 0) {
                hasSelectedDomains = true;
            }
            return selRows || hasSelectedDomains;
        }
        return selRows;
    }

    selectedNodeChangedCallback = (node: GenericTreeNode) => {
        if (node) {
            this.selectedNode = node;
        }
    }

    loadExportComparison(hasCorpusSetCreatePermissions: boolean): void {
        this.nodes.push(new GenericTreeNode({
            name: 'Exports',
            automationId: this.modalAutomationId + '-exports',
            entityType: EntityType.export,
            isExpanded: true,
            isExpandable: true,
            isExpandedOnSelect: true,
            isSelectable: false,
            children: [],
            data: this.commonService.projectData
        }));
        this.tree.setData(this.nodes);
        // dynamic export comparison menu
        this.nodes[0].isLoading = true;
        if (!hasCorpusSetCreatePermissions) {
            this.restService.post(Endpoint.workingSetManager_listExportStreams, {
                count: 50,
                filters: [
                    { attribute: 'reviewProductions', value: 'false', operator: 'EQUALS' },
                    { attribute: 'reviewStream', value: 'false', operator: 'EQUALS' }
                ],
                handle: this.commonService.projectData.handle,
                startIndex: 0,
            }).then(response => {
                this.nodes[0].isLoading = false;
                this.createExportTree(response);
            }, error => {
                this.nodes[0].isLoading = false;
            });
        } else {
            this.restService.post(Endpoint.ecaManager_listExportStreams, {
                count: 50,
                startIndex: 0,
                requestHandle: this.commonService.projectData.handle,
                handle: this.commonService.currentProject.handle,
            }).then(response => {
                this.nodes[0].isLoading = false;
                this.createExportTree(response);
            }, error => {
                this.nodes[0].isLoading = false;
            });
        }
    }

    createExportTree(response): void {
        if (response.exportStreams && response.exportStreams.length > 0) {
            response.exportStreams.forEach(el => {
                const node = new GenericTreeNode({
                    name: el.name,
                    customNumber: el.documentCount,
                    icon: 'fa-cube',
                    iconColor: 'color-blue-03',
                    entityType: EntityType.export,
                    hasExpanderPadding: false,
                    data: el, // new ExportStream(el);
                    automationId: 'target-picker-tree-exports-' + el.name.replace(/ /g, '-')
                });
                this.nodes[0].children.push(node);
            });
        }
        this.tree.treeDataUpdated();
    }

    filterData(str: string): void {
        if (str) {
            if (!this.tree.treeControl.isExpanded(this.nodes[0])) {
                this.tree.treeControl.expand(this.nodes[0]);
            }
            this.nodes[0].filterBy = node => node.name.toLowerCase().includes(str) || node.isButton;
        } else {
            this.nodes[0].filterBy = null;
            if (this.targetType === this.modalEntityType.EXPORT_COMPARISON.type) {
                this.tree.treeControl.expandAll();
            } else {
                this.tree.treeControl.collapseAll();
            }
        }
        this.tree.treeDataUpdated();
        this.tree.selectedNode = null;
        this.selectedNode = null;
    }

    isPrimaryButtonEnabled(): boolean {
        return (this.selectedRows() || this.selectedNode) && !this.isBusy;
    }

    doubleClickAction(node: GenericTreeNode) {
        this.ok();
    }

    ok(): void {
        switch (this.targetType) {
            case this.modalEntityType.EXPORT_COMPARISON.type: {
                const exportComparison = {
                    export: this.selectedNode,
                    generateSearchTermReport: this.includeSearchTermReport,
                    provideExactGeneratedDocSizes: this.provideHTMLSizes,
                    selectedTreeItem: this.modalData.items && this.modalData.items.handle ? this.modalData.items.handle : this.commonService.selectedNavigationNode.data.handle,
                    taskDescription: 'Export Comparison Report: ' + this.selectedNode.name + ' Report'
                };
                this.closeModal(true, exportComparison);
                break;
            }
            case this.modalEntityType.DOMAINS.type: {
                // Save current right grid selections before closing
                if (this.selectedList?.handle) {
                    this.saveCurrentDomainSelections();
                }

                // Categorize selections into three types:
                // 1. Fully selected domain lists (all domains selected) - show DL name in query
                // 2. Partially selected domain lists (some domains selected) - show individual domain names
                // 3. Whole domain list selections (empty lists or selected without individual domains) - show DL name

                // Partially selected domains (individual domain names to show in query)
                const partiallySelectedDomains: { name: string; domainListHandle?: string; domainListName?: string }[] = [];

                // Fully selected domain lists (DL names to show in query)
                const fullySelectedLists: { name: string; handle: string; isFullySelected: boolean }[] = [];

                // Track which handles we've already processed to avoid duplicates
                const processedHandles = new Set<string>();

                this.selectedDomainsPerList.forEach((domains, domainListHandle) => {
                    if (domains.length > 0) {
                        const totalDomains = this.totalDomainsPerList.get(domainListHandle) || 0;
                        let domainListName = '';
                        this.genericGrids.first?.gridApi?.forEachNode(node => {
                            if (node.data.handle === domainListHandle) {
                                domainListName = node.data.name;
                            }
                        });

                        if (totalDomains > 0 && domains.length === totalDomains) {
                            // All domains selected - add to fully selected lists (show DL name)
                            fullySelectedLists.push({
                                name: domainListName,
                                handle: domainListHandle,
                                isFullySelected: true
                            });
                            processedHandles.add(domainListHandle);
                        } else {
                            // Partial selection - add individual domain names
                            domains.forEach(domainName => {
                                partiallySelectedDomains.push({
                                    name: domainName,
                                    domainListHandle: domainListHandle,
                                    domainListName: domainListName
                                });
                            });
                            processedHandles.add(domainListHandle);
                        }
                    }
                });

                // Also add domain lists from fullySelectedDomainLists map that weren't already processed
                // (these are DLs that were checked but not visited, so marked for full selection)
                this.fullySelectedDomainLists.forEach((value, handle) => {
                    // Skip if already processed from selectedDomainsPerList
                    if (!processedHandles.has(handle)) {
                        fullySelectedLists.push({
                            name: value.name,
                            handle: handle,
                            isFullySelected: true
                        });
                        processedHandles.add(handle);
                    }
                });

                // Collect whole domain list selections (empty lists or lists selected without domains)
                // Skip any already processed
                const selectedWholeLists: { name: string; handle: string; isWholeDomainList: boolean }[] = [];
                this.selectedWholeDomainLists.forEach((value, handle) => {
                    if (value.selected && !processedHandles.has(handle)) {
                        selectedWholeLists.push({
                            name: value.name,
                            handle: handle,
                            isWholeDomainList: true
                        });
                        processedHandles.add(handle);
                    }
                });

                // Return structured data with all selection categories
                const result = {
                    partiallySelectedDomains: partiallySelectedDomains,  // Individual domain names
                    fullySelectedDomainLists: fullySelectedLists,        // DL names (all domains selected)
                    selectedWholeDomainLists: selectedWholeLists,        // DL names (empty or whole list selected)
                    // Maps for restoring state when modal is reopened
                    selectedDomainsPerList: Object.fromEntries(this.selectedDomainsPerList),
                    totalDomainsPerList: Object.fromEntries(this.totalDomainsPerList),
                    selectedWholeDomainListsMap: Object.fromEntries(this.selectedWholeDomainLists),
                    fullySelectedDomainListsMap: Object.fromEntries(this.fullySelectedDomainLists)
                };

                this.closeModal(true, result);
                break;
            }
            default: {
                this.closeModal(true, this.genericGrids.first.gridCheckedItems);
                break;
            }

        }
    }

    isAllDomainsSelected(): boolean {
        return this.selectedList ? this.selectedList.name === SYS_GENERATED_DOMAIN_LIST : false;
    }

    onCellClicked = (event: CellClickedEvent) => {
      this.loadRightGrid(event.data, event?.colDef?.colId);
    }

    loadRightGrid(row: any, coldId: string = '') {
        if ((this.targetType === this.modalEntityType.ALIAS.type || this.targetType === this.modalEntityType.DOMAINS.type) && coldId !== 'checkbox') {
            const isRowAlreadySelected = row.isRowSelected;

            // Save current right grid selections before switching domain lists
            if (this.targetType === this.modalEntityType.DOMAINS.type && this.selectedList?.handle) {
                this.saveCurrentDomainSelections();
            }

            this.genericGrids.first?.gridApi?.getModel().forEachNode(node => {
                node.data.isRowSelected = false;
            });
            if (!isRowAlreadySelected) {
                row.isRowSelected = true;
                this.selectedList = row;
                this.listEntries(row.handle, row.projectHandle);
                if (this.targetType === this.modalEntityType.DOMAINS.type) {
                    this.filterQuery = '';
                    if (this.isAllDomainsSelected()) {
                        this.modalData.items = [];
                    }
                    this.domainButtonDisabled = !this.isAllDomainsSelected();
                    this.genericGrids.last.gridApi.setGridOption('rowSelection', 'multiple');
                }
            } else {
                this.selectedList = null;
            }
            this.genericGrids.first?.gridApi?.redrawRows();
        }
    }

    // Save current domain selections for the currently selected domain list
    saveCurrentDomainSelections(): void {
        if (this.selectedList?.handle && this.genericGrids?.last?.gridApi) {
            const selectedDomains = this.genericGrids.last.gridApi.getSelectedRows().map(row => row.name);
            this.selectedDomainsPerList.set(this.selectedList.handle, selectedDomains);
            this.updateLeftGridCheckboxState(this.selectedList.handle);
        }
    }

    // Update the checkbox state of a domain list in the left grid based on its domain selections
    updateLeftGridCheckboxState(domainListHandle: string): void {
        const selectedDomains = this.selectedDomainsPerList.get(domainListHandle) || [];
        const totalDomains = this.totalDomainsPerList.get(domainListHandle) || 0;

        this.genericGrids.first?.gridApi?.forEachNode(node => {
            if (node.data.handle === domainListHandle && node.data.name !== SYS_GENERATED_DOMAIN_LIST) {
                const domainListName = node.data.name;

                if (selectedDomains.length === 0) {
                    // No domains selected - uncheck (unless it's a whole list selection or empty list)
                    const isWholeListSelected = this.selectedWholeDomainLists.get(domainListHandle)?.selected;
                    if (!isWholeListSelected) {
                        node.setSelected(false);
                    }
                    node.data.partiallySelected = false;
                    // Remove from fully selected list
                    this.fullySelectedDomainLists.delete(domainListHandle);
                } else if (totalDomains > 0 && selectedDomains.length === totalDomains) {
                    // All domains selected - fully checked (show DL name in query)
                    node.setSelected(true);
                    node.data.partiallySelected = false;
                    // Track as fully selected - will show DL name instead of individual domains
                    this.fullySelectedDomainLists.set(domainListHandle, { name: domainListName });
                    // Remove from whole list selection since it's now fully selected via domains
                    this.selectedWholeDomainLists.delete(domainListHandle);
                } else if (selectedDomains.length > 0) {
                    // Some domains selected - partially checked (show individual domain names in query)
                    node.setSelected(true);
                    node.data.partiallySelected = true;
                    // Remove from fully selected list
                    this.fullySelectedDomainLists.delete(domainListHandle);
                    // Remove from whole list selection
                    this.selectedWholeDomainLists.delete(domainListHandle);
                }
            }
        });
        this.genericGrids.first?.gridApi?.redrawRows();
    }

    // Restore domain selections in the right grid for the given domain list
    restoreDomainSelections(domainListHandle: string): void {
        if (!this.genericGrids?.last?.gridApi) {
            return;
        }

        // Check if this domain list is marked for full selection
        const isMarkedForFullSelection = this.fullySelectedDomainLists.has(domainListHandle);

        if (isMarkedForFullSelection) {
            // Select ALL domains when the grid loads
            setTimeout(() => {
                this.genericGrids.last?.gridApi?.selectAll();
            }, 100);
            return;
        }

        // Otherwise, restore individual domain selections
        const previouslySelectedDomains = this.selectedDomainsPerList.get(domainListHandle);
        if (previouslySelectedDomains && previouslySelectedDomains.length > 0) {
            // Use setTimeout to ensure grid data is fully loaded before selecting
            setTimeout(() => {
                this.genericGrids.last.gridApi.forEachNode(node => {
                    if (previouslySelectedDomains.includes(node.data.name)) {
                        node.setSelected(true);
                    }
                });
            }, 100);
        }
    }

    onSelectionChanged = (event?: SelectionChangedEvent) => {
        this.rowsSelected = event.api.getSelectedRows();
        if (this.targetType === this.modalEntityType.DOMAINS.type) {
            if (this.isAllDomainsSelected() && event.api.getSelectedRows().length > 0) {
                this.genericGrids.last?.gridApi?.deselectAll();
            }

            // Skip processing if we're in the middle of programmatic selection changes
            if (this.isProcessingLeftGridSelection) {
                return;
            }

            // Track whole domain list selections when checkbox is clicked on left grid
            const currentSelectedHandles = new Set<string>(event.api.getSelectedRows().map(row => row.handle));

            // Detect which handles changed (were newly selected or newly deselected)
            const newlySelected = new Set<string>();
            const newlyDeselected = new Set<string>();

            currentSelectedHandles.forEach(handle => {
                if (!this.previousLeftGridSelectedHandles.has(handle)) {
                    newlySelected.add(handle);
                }
            });

            this.previousLeftGridSelectedHandles.forEach(handle => {
                if (!currentSelectedHandles.has(handle)) {
                    newlyDeselected.add(handle);
                }
            });

            // Update the previous state for next comparison
            this.previousLeftGridSelectedHandles = new Set(currentSelectedHandles);

            // Only process if there was an actual change (checkbox click)
            if (newlySelected.size === 0 && newlyDeselected.size === 0) {
                return;
            }

            this.isProcessingLeftGridSelection = true;

            // For each domain list, check if it was selected/deselected via checkbox
            event.api.forEachNode(node => {
                const handle = node.data.handle;
                const name = node.data.name;
                const wasJustSelected = newlySelected.has(handle);
                const wasJustDeselected = newlyDeselected.has(handle);
                const wasPartiallySelected = node.data.partiallySelected;
                const hasIndividualDomains = (this.selectedDomainsPerList.get(handle)?.length || 0) > 0;
                const isFullySelected = this.fullySelectedDomainLists.has(handle);
                const totalDomains = this.totalDomainsPerList.get(handle) || 0;

                // Only process nodes that actually changed
                if (!wasJustSelected && !wasJustDeselected) {
                    // Special case: partial checkbox was clicked (ag-grid deselects it, we re-select)
                    if (wasPartiallySelected && !node.isSelected()) {
                        // This shouldn't happen normally, but handle it just in case
                    }
                    return;
                }

                // Handle checkbox click on the currently displayed domain list
                if (this.selectedList?.handle === handle) {
                    if (wasJustDeselected && wasPartiallySelected) {
                        // Clicking a partial checkbox should SELECT ALL, not deselect
                        node.setSelected(true);
                        this.genericGrids.last?.gridApi?.selectAll();
                        node.data.partiallySelected = false;
                        this.fullySelectedDomainLists.set(handle, { name });
                        this.selectedDomainsPerList.delete(handle);
                        // Update previous state since we re-selected
                        this.previousLeftGridSelectedHandles.add(handle);
                    } else if (wasJustSelected) {
                        // DL checkbox was checked - select ALL domains on right grid
                        this.genericGrids.last?.gridApi?.selectAll();
                        node.data.partiallySelected = false;
                    } else if (wasJustDeselected && !wasPartiallySelected) {
                        // DL checkbox was unchecked (and wasn't partial) - deselect ALL domains
                        this.genericGrids.last?.gridApi?.deselectAll();
                        this.selectedDomainsPerList.delete(handle);
                        this.fullySelectedDomainLists.delete(handle);
                        this.selectedWholeDomainLists.delete(handle);
                        node.data.partiallySelected = false;
                    }
                } else {
                    // Handle checkbox click on a domain list that is NOT currently displayed
                    if (wasJustDeselected && wasPartiallySelected) {
                        // Clicking a partial checkbox should mark for full selection
                        node.setSelected(true);
                        this.fullySelectedDomainLists.set(handle, { name });
                        node.data.partiallySelected = false;
                        this.selectedDomainsPerList.delete(handle);
                        // Update previous state since we re-selected
                        this.previousLeftGridSelectedHandles.add(handle);
                    } else if (wasJustSelected && !hasIndividualDomains && !isFullySelected) {
                        // DL checkbox was checked but we haven't loaded its domains yet
                        this.fullySelectedDomainLists.set(handle, { name });
                        node.data.partiallySelected = false;
                    } else if (wasJustDeselected && !wasPartiallySelected) {
                        // DL checkbox was unchecked (and wasn't partial)
                        this.selectedDomainsPerList.delete(handle);
                        this.fullySelectedDomainLists.delete(handle);
                        this.selectedWholeDomainLists.delete(handle);
                        node.data.partiallySelected = false;
                    }
                }

                // Track whole domain list selections (empty lists)
                if (node.isSelected() && totalDomains === 0 && !hasIndividualDomains && !isFullySelected) {
                    this.selectedWholeDomainLists.set(handle, { name, selected: true });
                } else if (!node.isSelected()) {
                    this.selectedWholeDomainLists.delete(handle);
                }
            });

            this.isProcessingLeftGridSelection = false;
            this.genericGrids.first?.gridApi?.redrawRows();
        }
    }

    onRightGridSelectionChanged = (event?: SelectionChangedEvent) => {
        if (event?.api && this.genericGrids?.last?.gridApi) {
            this.rightGridSelectedRows = this.genericGrids.last.selectedRows;
            if (this.isAllDomainsSelected() && event.api.getSelectedRows().length > 0) {
                this.genericGrids.first?.gridApi?.deselectAll();
            }

            // Track domain selections for the current domain list and update left grid checkbox state
            if (this.targetType === this.modalEntityType.DOMAINS.type && this.selectedList?.handle) {
                const selectedDomains = event.api.getSelectedRows().map(row => row.name);
                this.selectedDomainsPerList.set(this.selectedList.handle, selectedDomains);

                // Prevent onSelectionChanged from triggering selectAll when we update the left grid checkbox
                this.isProcessingLeftGridSelection = true;
                this.updateLeftGridCheckboxState(this.selectedList.handle);
                // Update the previous state to match the new selection state
                if (this.genericGrids.first?.gridApi) {
                    this.previousLeftGridSelectedHandles = new Set<string>(
                        this.genericGrids.first.gridApi.getSelectedRows().map(row => row.handle)
                    );
                }
                this.isProcessingLeftGridSelection = false;
            }
        }
    }

    openDomainButton(isAddToExisting: boolean = false) {
        if (this.genericGrids?.last?.selectedRows?.length > 0) {
            const selectedDomains = this.genericGrids.last.selectedRows.map(item => item.name);
            if (selectedDomains?.length > 0) {
                const newDomainList = new DomainList({ projectHandle: this.commonService.currentProject.handle, domainEntries: selectedDomains });
                Object.assign(newDomainList, { isSystemUser: this.commonService.currentUser.isSuperSystemUser });
                if (isAddToExisting) {
                    this.commonService.openModal((AddSelectedTextToModalComponent), {
                        modalData: <AddSelectedTextToModalData>{
                            type: AddSelectedTextToModalType.DomainList,
                            selectedDomains: selectedDomains,
                            isAddToExistingDomainList: true
                        },
                        class: 'vertical-percent-100 horizontal-percent-100'
                    }).then((response) => {
                        if (response && response.modalData && response.modalData.domain) {
                            this.selectedList = response.modalData.domain;
                            this.genericGrids.first.loadData();
                        }
                    });
                } else {
                    this.commonService.openModal((CreateEditEntityModalComponent), {
                        modalData: newDomainList
                    }).then((response) => {
                        if (response && response.modalData && response.modalData.domain) {
                            this.selectedList = response.modalData.domain;
                            this.rowsSelected = [response.modalData.domain];
                            this.genericGrids.first.loadData();
                            this.loadRightGrid(response.modalData.domain);
                        }
                    });
                }
            }
        }
    }

    listEntries(domainHandle: string, requestHandle: string) {
        this.isBusyEntries = true;
        this.isBusyMessageEntries = 'Loading ' + this.columnDefsLists[0]?.headerName + ' ...';

        if (this.targetType === this.modalEntityType.DOMAINS.type) {
            this.isBusyMessageEntries = 'Loading domains ...';
            const currentDomainHandle = this.selectedList ? this.selectedList.handle : null;
            this.domainsCount = '';
            if (domainHandle && requestHandle) {
                this.restService.post(Endpoint.projectManager_listDomainEntries, {
                    domainHandle: domainHandle,
                    requestHandle: requestHandle,
                    maxEntries: DOMAINS_MAX_DISPLAY_GRID_ENTRIES,
                    filters: this.filterQuery ? [{
                        attribute: 'name',
                        value: this.filterQuery,
                        operator: 'LIKE'
                    }] : []
                }).then(res => {
                    if (res?.domainEntries?.length > 0 && currentDomainHandle === this.selectedList.handle) {
                        res.domainEntries = res.domainEntries.sort();
                        const domains = [];
                        res.domainEntries.forEach((domain, index) => {
                            return domains[index] = {
                                name: domain
                            };
                        });

                        this.currentEntries = domains;
                        if (this.filterQuery !== '' && !this.needsBackendFiltering) {
                            this.onFilterApply(this.filterQuery);
                        } else {
                            this.needsBackendFiltering = false;
                            this.genericGrids.last.setData(domains);
                        }

                        // Store total domain count for this domain list
                        this.totalDomainsPerList.set(currentDomainHandle, res.totalNumberOfEntries);

                        // Restore previously selected domains for this domain list
                        this.restoreDomainSelections(currentDomainHandle);

                    } else {
                        this.currentEntries = [];
                        this.genericGrids.last.setData(this.currentEntries);
                    }
                    this.isBusyEntries = false;
                    this.domainsCount = '1 - ' + res.domainEntries?.length + ' / ' + res.totalNumberOfEntries;
                    this.domainEntriesTotalCount = res.totalNumberOfEntries;
                }, error => {
                    this.isBusyEntries = false;
                });
            }
        } else if (this.targetType === 'alias-list') {
            const al = [];
            if (this.selectedList?.aliases) {
                this.selectedList.aliases.forEach((alias, index) => {
                    return al[index] = {
                        aliasName: alias
                    };
                });
            }
            this.currentEntries = al;
            this.genericGrids.last.setData(al);
            if (this.filterQuery !== '') {
                this.onFilterApply(this.filterQuery);
            } else {
                this.genericGrids.last.setData(this.currentEntries);
            }
            this.isBusyEntries = false;
        }
    }

    onFilterApply(queryStr: string, filterType: FilterTemplateType = FilterTemplateType.freeform, filterColumnName: string = ''): void {
        if (filterType === FilterTemplateType.clear && this.genericGrids?.last) {
            this.genericGrids.last.onFilterApply(queryStr, filterType, filterColumnName);
        }
        this.filterQuery = queryStr;
        const fieldType = this.targetType === this.modalEntityType.DOMAINS.type ? 'name' : 'aliasName';
        if (fieldType === 'name' && this.domainEntriesTotalCount > DOMAINS_MAX_DISPLAY_GRID_ENTRIES && this.selectedList) {
            this.needsBackendFiltering = true;
            this.listEntries(this.selectedList.handle, this.selectedList.projectHandle);
        } else {
            if (this.genericGrids?.last) {
                const filtered = _.filter(this.currentEntries, (field) => field[fieldType] ? field[fieldType]?.toLowerCase()?.indexOf(queryStr?.toLowerCase()) !== -1 : false);
                this.genericGrids.last.setData(filtered);
            }
        }
    }

    onLeftFilterApply(queryStr: string, filterType: FilterTemplateType = FilterTemplateType.freeform, filterColumnName: string = ''): void {
        if(queryStr === '') {
            this.rowsSelected = this.genericGrids.first.selectedRows;
        }
        this.genericGrids.first.onFilterApply(queryStr, filterType, filterColumnName);
        const selectedRow = this.selectedRowVisible();
        if (selectedRow) {
           if (!this.selectedList) {
                this.loadRightGrid(selectedRow.data);
           }
        }
    }

    selectedRowVisible() {
        return this.genericGrids.first.gridApi?.getRenderedNodes().find(node => { return node.data.isRowSelected })
    }

    onMetadataFilterApply(queryStr: string, filterType: FilterTemplateType = FilterTemplateType.freeform, filterColumnName: string = ''): void {
        this.filterQuery = queryStr;
        const metadataObject = this.modalData.metadataObject;
        if (queryStr?.trim() === '' || filterType === FilterTemplateType.clear) {
            if (filterType === FilterTemplateType.clear) {
                this.genericGrids.first.onFilterApply(queryStr, filterType, filterColumnName);
            }
            this.genericGrids.first.setData(this.currentEntries);
            this.setLeftGridEntriesCount(this.currentEntries?.length, this.leftListTotalCount);
        } else {
            if (this.needsBackendFiltering && this.commonService.projectData?.handle && metadataObject) {
                const filtersList = [];
                filtersList.push({
                    attribute: metadataObject.field,
                    value: queryStr,
                    operator: 'LIKE'
                });
                const requestData = { viewHandle: this.commonService.projectData.handle, metadataFields: metadataObject.field, filters: filtersList, maxEntries: METADATA_MAX_DISPLAY_GRID_ENTRIES };
                this.restService.post(Endpoint.projectManager_getMetadataValues, requestData).then((response) => {
                    if (response?.metadataValues) {
                        const filteredList = [];
                        let entriesCount = 0;
                        const metadataObj = response.metadataValues.find(met => met.field === metadataObject.field);
                        if (metadataObj.values?.length > 0) {
                            filteredList.push(...metadataObj.values.map((m: string) => { return { name: m } }));
                        }
                        this.setLeftGridEntriesCount(filteredList.length, this.leftListTotalCount);
                        this.genericGrids.first.setData(filteredList);
                    }
                });
            } else {
                const filtered = _.filter(this.currentEntries, (field) => field.name ? field.name.toLowerCase().indexOf(queryStr?.toLowerCase()) !== -1 : false);
                this.genericGrids.first.setData(filtered);
                this.setLeftGridEntriesCount(filtered.length, this.leftListTotalCount);
            }
        }
    }

    setLeftGridEntriesCount(numberOfDisplayedEntries: number, totalNumberOfEntries: number) {
        this.leftGridEntriesCount = '1 - ' + numberOfDisplayedEntries + ' / ' + totalNumberOfEntries;
    }
}
